import {appApi} from "./api.service";
import {
    Defect,
    DefectGroup, LogisticContainer,
    Order,
    Report,
    ReportRow,
    ReportRowComponent, ReportRowFile,
    ReportRowMeasurement,
    ReportRowWorker
} from "../app/reportPage/reportPage.component";
import {Preferences} from "@capacitor/preferences";
import {Filter, FilterParam, FilterType} from "../app/reportPage/components/filter/filterActionSheet.component";
import {Profile} from "../models/profile";
import {ProfileType} from "../enums/profileType";
import {ReportSetting} from "../enums/report";
import {DataSelectable} from "../app/reportPage/components/report/reportRowForm.component";
import {changeBase46ToBlob, uploadFile} from "./file.service";
import {getDefaultOffset, getNumberOfWorkDays, getUserTzOffset, nextMoment, prevMoment} from "../utils/tools/time";
import {getTimepickerMinutesIterations} from "../utils/tools/timepicker";
import {TimepickerMinutesIterationsType} from "../enums/timepickerMinutesIterationsType";
import {PricingType} from "../enums/pricing";
import {getCurrentNetworkStatus} from "./offlineSync.service";
import {v4 as uuidv4} from 'uuid';

const MomentRange = require('moment-range');
const Moment = require('moment-timezone')
const moment = MomentRange.extendMoment(Moment);
moment.tz.setDefault('Europe/Warsaw');
const momentDurationFormatSetup = require("moment-duration-format");
momentDurationFormatSetup(moment);

let endpointPrefix = 'reports';

const getReports = async (status: string, params: any) => {
    let profileId = await Preferences.get({'key': 'profile_id'});

    return appApi.get(endpointPrefix + '/list/' + status + '?profileId=' + profileId.value + '&' + params);
}

const getReport = async (reportId: number | string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/' + reportId + '/view?profileId=' + profileId.value);
}

const checkSpecification = async (id: bigint | string, reportId = '', skipDetailedCheck: boolean = false) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    let allowNotNewest = 0;
    if (reportId && parseInt(reportId) !== 0) {
        allowNotNewest = 1;
    }
    return appApi.get(endpointPrefix + '/check-specification/' + id + '?allowNotNewest=' + allowNotNewest + '&profileId=' + profileId.value + '&skipDetailedCheck=' + (skipDetailedCheck ? '1' : '0'));
}

const findSpecificationByNumber = async (number: bigint | string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/find-specification/' + number + '?profileId=' + profileId.value);
}

const findContainerById = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/find-container/' + id);
}

const findContainerByNumber = async (number: bigint | string, orderId: bigint | string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/find-container-by-number/' + number + '?profileId=' + profileId.value + '&orderId=' + orderId);
}

const findOrderByNumber = async (number: bigint | string, skipDetailedCheck: boolean = false) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/check-order?orderNumber=' + number + '&profileId=' + profileId.value + '&skipDetailedCheck=' + (skipDetailedCheck ? '1' : '0'));
}

const findOrderById = async (id: bigint | string, skipDetailedCheck: boolean = false) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/check-order?orderId=' + id + '&profileId=' + profileId.value + '&skipDetailedCheck=' + (skipDetailedCheck ? '1' : '0'));
}

const saveReport = async (order: Order, data: Report, reportId?: string, draft = false, skipLimits: boolean = false, skipPerformance: boolean = false, skipWorkerHoursLimit: boolean = false, offline = false, offlineSync = false, logisticContainer = '0', closeContainer: boolean = false, skipValidation: boolean = false, editionStartAt?: string) => {
    if (reportId && isReportId(reportId) && !offline) {
        return editReport(reportId, data, draft, order, skipLimits, skipPerformance, skipWorkerHoursLimit, offline, offlineSync, logisticContainer, closeContainer, skipValidation, editionStartAt);
    } else {
        return postReport(order.id, data, draft, order, skipLimits, skipPerformance, skipWorkerHoursLimit, offline, offlineSync, logisticContainer, closeContainer, skipValidation);
    }
}

const postReport = async (orderId: number | string, data: Report, draft = false, order: Order, skipLimits: boolean = false, skipPerformance: boolean = false, skipWorkerHoursLimit: boolean = false,  offline = false, offlineSync = false, logisticContainer = '0', closeContainer: boolean = false, skipValidation: boolean = false) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    data = await preUploadOfflineFiles(data)
    return appApi.post(endpointPrefix + '/save-order-raport/' + orderId +
        '?profileId=' + profileId.value +
        '&draft=' + (draft ? 1 : 0) +
        '&internalCosts=' + (data.internalCostType ? 1 : 0) +
        '&skipPerformance=' + (skipPerformance ? 1 : 0) +
        '&skipWorkerHoursLimit=' + (skipWorkerHoursLimit ? 1 : 0) +
        '&skipLimits=' + (skipLimits ? 1 : 0) +
        '&logisticContainer=' + logisticContainer +
        '&closeContainer=' + (closeContainer ? 1 : 0) +
        '&ep2OfflineReport=' + (offlineSync || offline ? 1 : 0) +
        '&skipValidation=' + (skipValidation ? 1 : 0),
        transformReportToApiFormat(data, order, profileId.value, offlineSync), {
        headers: {
            'No-Loader': true
        }
    });
}

const editReport = async (reportId: number | string, data: Report, draft = false, order: Order, skipLimits: boolean = false, skipPerformance: boolean = false, skipWorkerHoursLimit: boolean = false, offline = false, offlineSync = false, logisticContainer = '0', closeContainer: boolean = false, skipValidation: boolean = false, editionStartAt?: string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    data = await preUploadOfflineFiles(data)
    return appApi.post(endpointPrefix + '/edit-order-raport/' + reportId +
        '?profileId=' + profileId.value +
        '&draft=' + (draft ? 1 : 0) +
        '&internalCosts=' + (data.internalCostType ? 1 : 0) +
        '&skipPerformance=' + (skipPerformance ? 1 : 0) +
        '&skipWorkerHoursLimit=' + (skipWorkerHoursLimit ? 1 : 0) +
        '&skipLimits=' + (skipLimits ? 1 : 0) +
        '&logisticContainer=' + logisticContainer +
        '&closeContainer=' + (closeContainer ? 1 : 0) +
        '&ep2OfflineReport=' + (offlineSync || offline ? 1 : 0) +
        '&skipValidation=' + (skipValidation ? 1 : 0) +
        '&editionStartAt=' + editionStartAt,
        transformReportToApiFormat(data, order, profileId.value, offlineSync), {
        headers: {
            'No-Loader': true
        }
    });
}

const preUploadOfflineFiles = async (data: Report) => {
    for (const r of data.rows) {
        const i = data.rows.indexOf(r);
        let files: ReportRowFile[] = [];
        for (let file of r.files) {
            if (file.path) {
                files.push(file);
            } else if (file.offline_base64) {
                let blob = changeBase46ToBlob(file.offline_base64);
                await uploadFile(blob, file.original_name)
                    .then(response => {
                        let tmpFiles: ReportRowFile[] = files;
                        for (let f of response.data.files) {
                            let file = f as ReportRowFile;
                            files.push(file)
                        }
                    });
            }
        }

        data.rows[i].files = files;
    }

    return data;
}

const transformReportToApiFormat = (data: Report, order: Order, profileId: string | number | null, offlineSync = false) => {
    return {
        order_report: {
            offlineSync: offlineSync,
            ep2AdditionalOrders: order.enable_single_components && order.enable_multiple_order_reports && data.additionalOrders ? data.additionalOrders.map((order) => {
                return {
                    specification: order.specification.id,
                };
            }) : null,
            serviceDate: moment(data.serviceDate).format('YYYY-MM-DD'),
            ep2ScannedSpecificationId: data.specificationId,
            someBasketWorker: data.someBasketWorker,
            logisticContainerAdditionalNumber: data.logisticContainerAdditionalNumber,
            hoursAlertComment: data.hoursAlertComment,
            internalCostsType: data.internalCostType?.id,
            rows: data.rows.map((row) => {
                return {
                    id: row.id ? row.id : null,
                    serviceDate: moment(data.serviceDate).format('YYYY-MM-DD'),
                    deliveryDate: row.deliveryDate ? moment(row.deliveryDate).format('YYYY-MM-DD') : null,
                    checkedParts: row.quantity,
                    componentNumber: data.additionalOrders && data.additionalOrders.length ? null : (row.component ? row.component.id : null),
                    comment: row.comments.comment,
                    additionalInfo: row.comments.partNumber,
                    additionalInfo2: row.comments.additionalInfoA,
                    additionalInfo3: row.comments.additionalInfoB,
                    defectGroups: !order.is_extended_reporting ? row.defectGroups.map((defectGroup) => {
                        let defectiveParts = defectGroup.quantity;
                        if (order.sub_type === 'OPTIMIZATION' && defectGroup.notRepaired !== undefined && defectGroup.repaired !== undefined) {
                            defectiveParts = defectGroup.notRepaired + defectGroup.repaired;
                        }

                        return {
                            defectiveParts: defectiveParts,
                            notRepairedParts: defectGroup.notRepaired,
                            repairedParts: defectGroup.repaired,
                            defects: defectGroup.defects?.map((defect) => {
                                return {
                                    defect: !defect.other ? defect.id : null,
                                    other: defect.other
                                };
                            })
                        }
                    }) : [],
                    files: row.files.map((file) => {
                        return {
                            path: file.path,
                            originalName: file.original_name,
                            hash: file.hash,
                            extension: file.extension,
                            mimeType: file.mime_type,
                            size: file.size,
                        }
                    }),
                    components: order.is_extended_reporting ? row.components?.slice().reverse().map((component: ReportRowComponent) => {
                        return {
                            id: component.id,
                            apiCreatedAt: component.createdAt ? moment(component.createdAt).format('YYYY-MM-DD HH:mm:ss') : null,
                            comment: component.comment,
                            componentNumberText: component.componentNumberText,
                            checkedParts: component.quantity,
                            deliveryNote: component.deliveryNote,
                            levNumber: component.levNumber,
                            serialNumber: component.serialNumber,
                            batchNumber: component.batchNumber,
                            materialNumber: component.materialNumber,
                            files: component.files.map((file) => {
                                return {
                                    path: file.path,
                                    originalName: file.original_name,
                                    hash: file.hash,
                                    extension: file.extension,
                                    mimeType: file.mime_type,
                                    size: file.size,
                                }
                            }),
                            defectGroups: component.defectGroups.map((defectGroup) => {
                                let defectiveParts = defectGroup.quantity;
                                if (order.sub_type === 'OPTIMIZATION' && defectGroup.notRepaired !== undefined && defectGroup.repaired !== undefined) {
                                    defectiveParts = defectGroup.notRepaired + defectGroup.repaired;
                                }

                                return {
                                    defectiveParts: defectiveParts,
                                    notRepairedParts: defectGroup.notRepaired,
                                    repairedParts: defectGroup.repaired,
                                    defects: defectGroup.defects?.map((defect) => {
                                        return {
                                            defect: !defect.other ? defect.id : null,
                                            other: defect.other
                                        }
                                    })
                                }
                            }),
                        };
                    }) : undefined,
                    measurements: row.measurements?.map((measurement) => {
                        return {
                            measuringEquipmentItemCategory: measurement.measuring_equipment_item_category?.id,
                            measuringEquipmentOrderItem: !measurement.hide ? measurement.measuring_equipment_order_item?.id : null,
                            multiplier: measurement.multiplier,
                            hide: measurement.hide,
                            missing: !measurement.hide ? measurement.missing : false,
                            description: !measurement.hide ? measurement.description : null
                        }
                    }),
                    workers: row.workers.map((worker) => {
                        return {
                            id: worker.id ? worker.id : null,
                            worker: worker.personId,
                            startAt: moment(worker.from).format('HH:mm'),
                            endAt: worker.to ? moment(worker.to).format('HH:mm') : null,
                            globalPricingItem: profileId ? worker.pricingRate?.id : null,
                            rate: profileId ? worker.rate?.id : null,
                            workerOvertime: profileId ? worker.workerOvertime : null,
                            clientOvertime: profileId ? worker.clientOvertime : null,
                            overrideRate: worker.overrideRate ? worker.overrideRate : null,
                            workerBonus: worker.workerBonus ? worker.workerBonus : null,
                            bonus: worker.rate?.name?.includes('BONUS') ? true : null,
                            workerReportRateValue: worker.workerReportRateValue ? worker.workerReportRateValue : null,
                            withRateZero: worker.withRateZero !== undefined ? (worker.withRateZero ? '1' : '0') : null,
                        }
                    })
                }
            })
        }
    }
}

const transformApiFormatToReport = (data: any, addNewComponent: null | boolean = null, rowIndexNewComponent: null | number = null) => {
    let rows: ReportRow[] = [];
    let rowIndex = 0;
    for (let r of data.rows) {
        let workers: ReportRowWorker[] = [];
        for (let w of r.workers) {
            let worker = {
                id: w.id,
                avatarUrl: w.worker.user?.photos?.thumbnail ? w.worker.user?.photos?.thumbnail : (w.worker.user?.avatar ? w.worker.user?.avatar : undefined),
                personId: w.worker.id,
                name: w.worker.name,
                number: w.worker.number,
                from: moment(w.start_at, moment.ISO_8601).tz('Europe/Warsaw').format(),
                to: w.end_at ? moment(w.end_at, moment.ISO_8601).tz('Europe/Warsaw').format() : undefined,
                originalFrom: moment(w.start_at, moment.ISO_8601).tz('Europe/Warsaw').format(),
                originalTo: w.end_at ? moment(w.end_at, moment.ISO_8601).tz('Europe/Warsaw').format() : undefined,
                hoursHumanized: hoursHumanized(moment(w.start_at, moment.ISO_8601).tz('Europe/Warsaw').format('HH:mm'), moment(w.end_at, moment.ISO_8601).tz('Europe/Warsaw').format('HH:mm')),
                pricingRate: w.global_pricing_item,
                rate: w.rate,
                clientOvertime: w.client_overtime,
                workerOvertime: w.worker_overtime,
                withRateZero: w.with_rate_zero,
                virtualWorker: w.worker.virtual_worker,
            };
            workers = [...workers, worker];
        }

        let rowDefects: DefectGroup[] = [];
        for (let dg of r.defect_groups) {
            let defects: Defect[] = [];
            for (let d of dg.defects) {
                let defect = {
                    id: !d.other ? d.defect.id : d.other,
                    name: !d.other ? d.defect.name : d.other,
                    other: d.other,
                    defect: d.defect,
                };

                defects = [...defects, defect];
            }

            let defectGroup = {
                quantity: dg.defective_parts,
                repaired: dg.repaired_parts,
                notRepaired: dg.not_repaired_parts,
                defects: defects,
            };
            rowDefects = [...rowDefects, defectGroup];
        }

        let components: ReportRowComponent[] = [];

        if (addNewComponent && rowIndexNewComponent !== null && rowIndexNewComponent === rowIndex) {
            const maxIndex = components.reduce((max, comp) => {
                const currentId = comp.index;
                if (currentId != null) {
                    return isNaN(currentId) ? max : Math.max(max, currentId);
                }
                return 0;
            }, 0);

            let newComponent = {
                comment: '',
                defectGroups: [],
                files: [],
                componentNumberText: '',
                quantity: '1',
                deliveryNote: '',
                levNumber: '',
                serialNumber: '',
                batchNumber: '',
                materialNumber: '',
                new: true,
                key: uuidv4(),
                index: maxIndex + 1,
                createdAt: moment().format(),
            } as ReportRowComponent;

            components = [...components, newComponent];
        }

        for (let c of r.components) {
            let index = r.components.indexOf(c);
            if (addNewComponent && rowIndexNewComponent !== null && rowIndexNewComponent === rowIndex) {
                index++;
            }
            let componentDefects: DefectGroup[] = [];
            for (let dg of c.defect_groups) {
                let defects: Defect[] = [];
                for (let d of dg.defects) {
                    let defect = {
                        id: !d.other ? d.defect.id : d.other,
                        name: !d.other ? d.defect.name : d.other,
                        other: d.other,
                        defect: d.defect,
                    };

                    defects = [...defects, defect];
                }

                let defectGroup = {
                    quantity: dg.defective_parts,
                    repaired: dg.repaired_parts,
                    notRepaired: dg.not_repaired_parts,
                    defects: defects,
                };
                componentDefects = [...componentDefects, defectGroup];
            }

            let component = {
                defectGroups: componentDefects,
                files: c.files,
                comment: c.comment,
                componentNumberText: c.component_number_text,
                quantity: c.checked_parts,
                deliveryNote: c.delivery_note,
                levNumber: c.lev_number,
                serialNumber: c.serial_number,
                batchNumber: c.batch_number,
                materialNumber: c.material_number,
                id: c.id,
                index: index,
                createdAt: c.created_at ? moment(c.created_at, moment.ISO_8601).tz('Europe/Warsaw').format() : null,
            } as ReportRowComponent;

            components = [...components, component];
        }

        let measurements: ReportRowMeasurement[] = [];
        for (let m of r.measurements) {
            let measurement = {
                measuring_equipment_item_category: m.measuring_equipment_item_category,
                measuring_equipment_order_item: m.measuring_equipment_order_item,
                multiplier: m.multiplier,
                missing: m.missing,
                hide: m.hide,
                description: m.description
            };

            measurements = [...measurements, measurement]
        }

        let row = {
            id: r.id,
            key: r.id,
            removed: false,
            components: components,
            component: {
                id: r.component_number ? r.component_number.id : null,
                name: r.component_number ? r.component_number.number : null
            },
            quantity: r.checked_parts,
            defectGroups: rowDefects,
            files: r.files,
            measurements: measurements,
            comments: {
                comment: r.comment,
                partNumber: r.additional_info,
                additionalInfoA: r.additional_info2,
                additionalInfoB: r.additional_info3
            },
            deliveryDate: r.delivery_date ? moment(r.delivery_date, moment.ISO_8601).tz('Europe/Warsaw').format() : null,
            workers: workers
        };
        rows = [...rows, row];

        rowIndex++;
    }

    return {
        orderId: data.order.id,
        reportId: data.id,
        orderNumber: data.order.formatted_number.replace('-', '/'),
        orderFactoryNumber: data.order.factory_number ? data.order.factory_number.replace('-', '/') : null,
        logisticContainerComponent: data.logistic_container?.component_number,
        logisticContainer: data.logistic_container,
        serviceDate: moment(data.service_date, moment.ISO_8601).tz('Europe/Warsaw').format(),
        someBasketWorker: data.someBasketWorker,
        logisticContainerAdditionalNumber: data.logistic_container_additional_number,
        specificationId: data.ep2_scanned_specification_id ? data.ep2_scanned_specification_id : (data.order.specification ? data.order.specification.id : null),
        rows: rows,
        status: data.status,
        additionalOrders: data.additional_orders ? data.additional_orders : [],
        internalCostType: data.internal_costs_type,
    }
}

const validateReport = async (data: Report, order: Order, profile?: Profile, withoutSpecification: boolean = false, finalValidationCheck: boolean = false, logisticContainer: LogisticContainer | null = null, internalCosts: boolean = false) => {
    const networkStatus = await getCurrentNetworkStatus();

    if (internalCosts && !data.internalCostType?.id) {
        return false;
    }

    if (data.rows.length === 0) {
        return false;
    }

    if (logisticContainer) {
        if (logisticContainer?.additional_number && data.logisticContainerAdditionalNumber == '') {
            return false;
        }
    } else {
        if (data.logisticContainer?.additional_number && data.logisticContainerAdditionalNumber == '') {
            return false;
        }
    }

    for (let row of data.rows) {
        if (row.workers.length === 0) {
            return false;
        }

        if (order.specification?.hasMeasurements && !row.skipMeasurements && (!row.measurements || !validateMeasurements(row.measurements))) {
            return false;
        }

        if (order.is_extended_reporting) {
            if (!withoutSpecification && !internalCosts) {
                if (!row.component.id && data.additionalOrders && data.additionalOrders.length === 0) {
                    return false;
                }
            }

            if (row.components?.length === 0 && !internalCosts) {
                return false;
            }
            if (row.components) {
                for (let component of row.components) {
                    if (!withoutSpecification) {
                        if (!component.componentNumberText) {
                            return false;
                        }

                        if (isSettingEnabled(ReportSetting.SERIAL_NUMBER, order) && !component.serialNumber && isSettingEnabled(ReportSetting.SERIAL_NUMBER_REQUIRED, order)) {
                            return false;
                        }

                        if (isSettingEnabled(ReportSetting.LEV_NUMBER, order) && !component.levNumber && isSettingEnabled(ReportSetting.LEV_NUMBER_REQUIRED, order)) {
                            return false;
                        }

                        if (isSettingEnabled(ReportSetting.DELIVERY_NOTE, order) && !component.deliveryNote && isSettingEnabled(ReportSetting.DELIVERY_NOTE_REQUIRED, order)) {
                            return false;
                        }

                        if (isSettingEnabled(ReportSetting.BATCH_NUMBER, order) && !component.batchNumber && isSettingEnabled(ReportSetting.BATCH_NUMBER_REQUIRED, order)) {
                            return false;
                        }

                        if (component.componentNumberText && order.enable_nested_reporting && order.nested_reporting_components && order.nested_reporting_components.length && !order.nested_reporting_components.includes(component.componentNumberText)) {
                            return false;
                        }

                        if (!component.quantity && component.quantity !== 0) {
                            return false;
                        }
                    }

                    let sum = 0;
                    for (let defectGroup of component.defectGroups) {
                        if (defectGroup.defects) {
                            if (defectGroup.defects.length === 0) {
                                return false;
                            }

                            for (let defect of defectGroup.defects) {
                                if (!defect.id) {
                                    return false;
                                }
                            }
                        }

                        if (order.sub_type === 'OPTIMIZATION') {
                            if (!defectGroup.notRepaired && defectGroup.notRepaired !== 0) {
                                return false;
                            }

                            if (!defectGroup.repaired && defectGroup.repaired !== 0) {
                                return false;
                            }

                            sum += (defectGroup.notRepaired + defectGroup.repaired);
                        } else {
                            if (!defectGroup.quantity && defectGroup.quantity !== 0) {
                                return false;
                            }

                            sum += defectGroup.quantity;
                        }
                    }

                    if (component.quantity && sum > 0 && sum > component.quantity) {
                        return false;
                    }
                }
            }
        } else {
            if (!withoutSpecification && !internalCosts) {
                if (!row.component.id && ((data.additionalOrders && data.additionalOrders.length === 0) || profile)) {
                    return false;
                }

                if (!row.quantity && row.quantity !== 0) {
                    return false;
                }
            }

            let sum = 0;
            for (let defectGroup of row.defectGroups) {
                if (defectGroup.defects) {
                    if (defectGroup.defects.length === 0) {
                        return false;
                    }

                    for (let defect of defectGroup.defects) {
                        if (!defect.id) {
                            return false;
                        }
                    }
                }

                if (order.sub_type === 'OPTIMIZATION') {
                    if (!defectGroup.notRepaired && defectGroup.notRepaired !== 0) {
                        return false;
                    }

                    if (!defectGroup.repaired && defectGroup.repaired !== 0) {
                        return false;
                    }

                    sum += (defectGroup.notRepaired + defectGroup.repaired);
                } else {
                    if (!defectGroup.quantity && defectGroup.quantity !== 0) {
                        return false;
                    }

                    sum += defectGroup.quantity;
                }
            }

            if (row.quantity && sum > 0 && sum > row.quantity) {
                return false;
            }
        }

        for (let worker of row.workers) {
            if (!worker.from) {
                return false;
            }

            if (finalValidationCheck && !worker.to && (!networkStatus || data.offlineUuid)) {
                return false;
            }

            let from = moment(worker.from, moment.ISO_8601).format('HH:mm')
            if (worker.to) {
                let to = moment(worker.to, moment.ISO_8601).format('HH:mm')

                if (from == to) {
                    return false;
                }
            }

            if (profile && profile.type === ProfileType.COORDINATOR) {
                if (!worker.withRateZero) {
                    if (![PricingType.FIXED_PRICE, PricingType.FIXED_PRICE_PER_MONTH].includes(order.pricing_type) && !worker.pricingRate) {
                        return false;
                    }

                    if (!worker.rate) {
                        return false;
                    }
                }
            }

            if (!profile && validateHoursPassingMidnight(worker, data, order) !== true) {
                return false;
            }
        }
    }

    return true;
}

export const validateRowComponent = (component: ReportRowComponent, order: Order, withoutSpecification: boolean = false) => {
    if (!withoutSpecification) {
        if (!component.componentNumberText) {
            return false;
        }

        if (isSettingEnabled(ReportSetting.SERIAL_NUMBER, order) && !component.serialNumber && isSettingEnabled(ReportSetting.SERIAL_NUMBER_REQUIRED, order)) {
            return false;
        }

        if (isSettingEnabled(ReportSetting.LEV_NUMBER, order) && !component.levNumber && isSettingEnabled(ReportSetting.LEV_NUMBER_REQUIRED, order)) {
            return false;
        }

        if (isSettingEnabled(ReportSetting.DELIVERY_NOTE, order) && !component.deliveryNote && isSettingEnabled(ReportSetting.DELIVERY_NOTE_REQUIRED, order)) {
            return false;
        }

        if (isSettingEnabled(ReportSetting.BATCH_NUMBER, order) && !component.batchNumber && isSettingEnabled(ReportSetting.BATCH_NUMBER_REQUIRED, order)) {
            return false;
        }

        if (component.componentNumberText && order.enable_nested_reporting && order.nested_reporting_components.length && !order.nested_reporting_components.includes(component.componentNumberText)) {
            return false;
        }

        if (!component.quantity && component.quantity !== 0) {
            return false;
        }
    }

    let sum = 0;
    for (let defectGroup of component.defectGroups) {
        if (defectGroup.defects) {
            if (defectGroup.defects.length === 0) {
                return false;
            }

            for (let defect of defectGroup.defects) {
                if (!defect.id) {
                    return false;
                }
            }
        }

        if (order.sub_type === 'OPTIMIZATION') {
            if (!defectGroup.notRepaired && defectGroup.notRepaired !== 0) {
                return false;
            }

            if (!defectGroup.repaired && defectGroup.repaired !== 0) {
                return false;
            }

            sum += (defectGroup.notRepaired + defectGroup.repaired);
        } else {
            if (!defectGroup.quantity && defectGroup.quantity !== 0) {
                return false;
            }

            sum += defectGroup.quantity;
        }
    }

    if (component.quantity && sum > 0 && sum > component.quantity) {
        return false;
    }

    return true;
}

// detracted
const isEmptyReport = (report: Report, withoutSpecification: boolean = false, allowNotEmptyQuantity: boolean = false) => {
    return false;
}

const validateReportRowQuantity = (order: Order, data: ReportRow) => {
    let sum = 0;
    if (order.is_extended_reporting) {
        return true;
    }

    for (let defectGroup of data.defectGroups) {

        if (order.sub_type === 'OPTIMIZATION') {
            if (!defectGroup.notRepaired && defectGroup.notRepaired !== 0) {
                return false;
            }

            if (!defectGroup.repaired && defectGroup.repaired !== 0) {
                return false;
            }

            sum += (defectGroup.notRepaired + defectGroup.repaired);
        } else {
            if (defectGroup.quantity) {
                sum += defectGroup.quantity;
            }
        }
    }

    return !(sum > 0 && data.quantity && sum > data.quantity);
}

export const validateReportRowComponentQuantity = (order: Order, data: ReportRowComponent) => {
    let sum = 0;
    for (let defectGroup of data.defectGroups) {

        if (order.sub_type === 'OPTIMIZATION') {
            if (!defectGroup.notRepaired && defectGroup.notRepaired !== 0) {
                return false;
            }

            if (!defectGroup.repaired && defectGroup.repaired !== 0) {
                return false;
            }

            sum += (defectGroup.notRepaired + defectGroup.repaired);
        } else {
            if (defectGroup.quantity) {
                sum += defectGroup.quantity;
            }
        }
    }

    return !(sum > 0 && data.quantity && sum > data.quantity);
}

export const validateMeasurements = (measurements: ReportRowMeasurement[]) => {
    let isValid = true;
    for (let measurement of measurements) {
        if (!measurement.hide) {
            if (isValid && ((!measurement.measuring_equipment_order_item && measurement.missing && !measurement.description)) || (!measurement.measuring_equipment_order_item && !measurement.missing)) {
                isValid = false;
            }
        }
    }

    return isValid;
}


export const canScanNextComponent = (order: Order, row: ReportRow, withoutSpecification: boolean = false) => {
    if (row.components) {
        for (let component of row.components) {
            if (!withoutSpecification) {
                if (!component.componentNumberText) {
                    return false;
                }

                if (isSettingEnabled(ReportSetting.SERIAL_NUMBER, order) && !component.serialNumber && isSettingEnabled(ReportSetting.SERIAL_NUMBER_REQUIRED, order)) {
                    return false;
                }

                if (isSettingEnabled(ReportSetting.LEV_NUMBER, order) && !component.levNumber && isSettingEnabled(ReportSetting.LEV_NUMBER_REQUIRED, order)) {
                    return false;
                }

                if (isSettingEnabled(ReportSetting.DELIVERY_NOTE, order) && !component.deliveryNote && isSettingEnabled(ReportSetting.DELIVERY_NOTE_REQUIRED, order)) {
                    return false;
                }

                if (isSettingEnabled(ReportSetting.BATCH_NUMBER, order) && !component.batchNumber && isSettingEnabled(ReportSetting.BATCH_NUMBER_REQUIRED, order)) {
                    return false;
                }

                if (!component.quantity && component.quantity !== 0) {
                    return false;
                }
            }

            let sum = 0;
            for (let defectGroup of component.defectGroups) {

                if (defectGroup.defects) {
                    if (defectGroup.defects.length === 0) {
                        return false;
                    }

                    for (let defect of defectGroup.defects) {
                        if (!defect.id) {
                            return false;
                        }
                    }
                }

                if (order.sub_type === 'OPTIMIZATION') {
                    if (!defectGroup.notRepaired && defectGroup.notRepaired !== 0) {
                        return false;
                    }

                    if (!defectGroup.repaired && defectGroup.repaired !== 0) {
                        return false;
                    }

                    sum += (defectGroup.notRepaired + defectGroup.repaired);
                } else {
                    if (!defectGroup.quantity && defectGroup.quantity !== 0) {
                        return false;
                    }

                    sum += defectGroup.quantity;
                }
            }

            if (component.quantity && sum > 0 && sum > component.quantity) {
                return false;
            }
        }
    }

    return true;
}

function checkTimeOverlap(from1: number, to1: number, from2: number, to2: number): boolean {
    const crosses1 = to1 <= from1; // Pierwszy zakres przechodzi przez północ
    const crosses2 = to2 <= from2; // Drugi zakres przechodzi przez północ

    if (!crosses1 && !crosses2) {
        return from1 < to2 && from2 < to1;
    }

    if (crosses1 && crosses2) {
        return true;
    }

    if (crosses1) {
        return !(to1 <= from2 && to2 <= from1);
    } else {
        return !(to2 <= from1 && to1 <= from2);
    }
}

const validateTimeOverlap = (report: Report) => {
    const overlapped: string[] = [];
    const allWorkers: ReportRowWorker[] = [];

    // Zbierz wszystkich pracowników
    for (const row of report.rows) {
        for (const worker of row.workers) {
            allWorkers.push(worker);
        }
    }

    // Grupuj po personId (konwersja na string)
    const grouped = allWorkers.reduce((acc: Record<string, ReportRowWorker[]>, worker) => {
        if (worker.personId && worker.from && worker.to) {
            const pid = String(worker.personId);
            acc[pid] = acc[pid] || [];
            acc[pid].push(worker);
        }
        return acc;
    }, {});

    for (const [, workers] of Object.entries(grouped)) {
        for (let i = 0; i < workers.length; i++) {
            for (let j = i + 1; j < workers.length; j++) {
                const w1 = workers[i];
                const w2 = workers[j];

                const from1 = parseInt(moment(w1.from).format('HHmm'));
                const to1   = parseInt(moment(w1.to).format('HHmm'));
                const from2 = parseInt(moment(w2.from).format('HHmm'));
                const to2   = parseInt(moment(w2.to).format('HHmm'));

                const overlaps = checkTimeOverlap(from1, to1, from2, to2);

                if (overlaps) {
                    overlapped.push(w1.name);
                    overlapped.push(w2.name);
                }
            }
        }
    }

    return Array.from(new Set(overlapped));
};

const orderComponentsNumbers = async (orderId: number | string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/components-number/' + orderId + '?profileId=' + profileId.value);
}

const orderDefects = async (specificationId: number | string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/defects/' + specificationId + '?profileId=' + profileId.value);
}

const orderWorkers = async (orderId: number | string, serviceDate: string, page: number = 1, searchText: string = '') => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/workers/' + orderId + '/' + serviceDate + '?profileId=' + profileId.value + '&page=' + page + '&searchText=' + searchText);
}

const checkWorker = async (orderId: number | string, number: string, serviceDate: string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/check-worker/' + orderId + '/' + number + '/' + serviceDate + '?profileId=' + profileId.value);
}

const getWorkerData = async (orderId: string, number: string, serviceDate: string) => {
    let workerData: any = await checkWorker(orderId, number, serviceDate)
        .then(response => {
            if (response.status === 200) {
                return {
                    id: response.data.id,
                    name: response.data.name,
                    number: response.data.number,
                    user: response.data.user
                };
            } else {
                return null;
            }
        }).catch(reason => {
            return null
        });

    return workerData;
}

const getPlaceOfServices = (profileId: string | number) => {
    return appApi.get(endpointPrefix + '/place-of-services?profileId=' + profileId);
}

const getPlaceOfServiceDepartments = (placeOfServicesId: number, profileId: string | number) => {
    return appApi.get(endpointPrefix + '/place-of-service/' + placeOfServicesId + '/departments?profileId=' + profileId);
}

const getReportsCount = (status: string, profileId: string | number, params: any = null) => {
    return appApi.get(endpointPrefix + '/counts/' + status + '?profileId=' + profileId + '&' + params);
}

const getNewestSpecification = async (orderId: string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/newest-specification/' + orderId + '?profileId=' + profileId.value);
}

const getNewestImportantInfo = async (orderId: string) => {
    return appApi.get(endpointPrefix + '/newest-important-info/' + orderId);
}

const getPriceRates = async (orderId: number | string, reportId?: number | string | null, serviceData?: string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/price-rates/' + orderId + '?profileId=' + profileId.value + '&reportId=' + reportId + '&serviceDate=' + serviceData);
}

const getWorkerRates = async (orderId: number | string, number: string, serviceDate: string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/worker-rates/' + orderId + '/' + number + '/' + serviceDate + '?profileId=' + profileId.value);
}

export const getMeasuringEquipments = async (specificationId: number | string, serviceDate: string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/measuring-equipments/' + specificationId + '/' + serviceDate + '?profileId=' + profileId.value);
}

export const getMeasuringEquipmentsItems = async (specificationId: number | string, serviceDate: string, categoryId: number | string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/measuring-equipments-items/' + specificationId + '/' + serviceDate + '/' + categoryId + '?profileId=' + profileId.value);
}

export const removeComponent = async (componentId: number) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.post(endpointPrefix + '/remove-component/' + componentId + '?profileId=' + profileId.value);
}

const filtersToParamsArray = (filters: Filter[]) => {
    let params: string[] = [];
    filters.forEach((filter) => {
        switch (filter.type) {
            case FilterType.ORDER_NUMBER:
                params.push('orderNumber=' + encodeURIComponent(filter.value));
                break;

            case FilterType.COMPONENT_NUMBER:
                params.push('componentNumber=' + encodeURIComponent(filter.value));
                break;

            case FilterType.COMPONENT_NAME:
                params.push('componentName=' + encodeURIComponent(filter.value));
                break;

            case FilterType.PLACE_OF_SERVICE:
                params.push('placeOfService=' + encodeURIComponent(filter.value));
                break;

            case FilterType.PLACE_OF_SERVICE_DEPARTMENT:
                params.push('placeOfServiceDepartment=' + encodeURIComponent(filter.value));
                break;
        }
    });

    return params;
}

const getFilterTypeForParamName = (param: string) => {
    switch (param) {
        case 'orderNumber':
            return FilterType.ORDER_NUMBER;
        case 'componentNumber':
            return FilterType.COMPONENT_NUMBER;
        case 'componentName':
            return FilterType.COMPONENT_NAME;
        case 'placeOfService':
            return FilterType.PLACE_OF_SERVICE;
        case 'placeOfServiceDepartment':
            return FilterType.PLACE_OF_SERVICE_DEPARTMENT;
    }
}

const filtersToParamsObjects = (filters: Filter[]) => {
    let params: FilterParam[] = [];
    filters.forEach((filter) => {
        switch (filter.type) {
            case FilterType.ORDER_NUMBER:
                params.push({name: 'orderNumber', value: encodeURIComponent(filter.value)});
                break;

            case FilterType.COMPONENT_NAME:
                params.push({name: 'componentName', value: encodeURIComponent(filter.value)});
                break;

            case FilterType.COMPONENT_NUMBER:
                params.push({name: 'componentNumber', value: encodeURIComponent(filter.value)});
                break;

            case FilterType.PLACE_OF_SERVICE:
                params.push({name: 'placeOfService', value: encodeURIComponent(filter.value)});
                break;

            case FilterType.PLACE_OF_SERVICE_DEPARTMENT:
                params.push({name: 'placeOfServiceDepartment', value: encodeURIComponent(filter.value)});
                break;
        }
    });

    return params;
}

const filtersToParams = (filters: Filter[]) => {
    let params: string[] = filtersToParamsArray(filters);
    return params.join('&');
}

const hoursHumanized = (startAt: string, endAt: string) => {
    if (startAt > endAt) {
        startAt = moment().format('01-MM-YYYY') + ' ' + startAt;
        endAt = moment().format('02-MM-YYYY') + ' ' + endAt;
    } else {
        startAt = moment().format('01-MM-YYYY') + ' ' + startAt;
        endAt = moment().format('01-MM-YYYY') + ' ' + endAt;
    }

    let startAtMoment = moment(startAt, 'DD-MM-YYYY HH:mm'),
        endAtMoment = moment(endAt, 'DD-MM-YYYY HH:mm');

    let diff = moment.duration(endAtMoment.diff(startAtMoment));
    let hoursNumber = diff.asMinutes();
    if (hoursNumber > 0) {
        return diff.format("hh:mm", {
            trim: false
        })
    }
}

const minReportDate = (order: Order, report: Report, selectedProfile: Profile | undefined): string => {
    let serviceDate = moment(report.serviceDate, moment.ISO_8601).tz('Europe/Warsaw').format('YYYY-MM-DD');
    if (moment().format('YYYY-MM-DD') > moment(report.serviceDate, moment.ISO_8601).tz('Europe/Warsaw').format('YYYY-MM-DD')) {
        serviceDate = moment().tz('Europe/Warsaw').format('YYYY-MM-DD');
    }
    let daysBack = 0;
    if (selectedProfile && selectedProfile.type == ProfileType.COORDINATOR && order.days_back_coordinator) {
        daysBack = order.days_back_coordinator;
    } else if (selectedProfile && selectedProfile.type == ProfileType.TEAM_LEADER && order.days_back_team_leader) {
        daysBack = order.days_back_team_leader;
    }

    let returnDate = '';
    if (selectedProfile && (selectedProfile.type == ProfileType.COORDINATOR || selectedProfile.type == ProfileType.TEAM_LEADER) && report.reportId && report.status !== 'DRAFT') {
        returnDate = moment(report && report.serviceDate ? serviceDate + ' ' + moment().format('HH:mm:ss') : null).subtract(10, 'years').format('YYYY-MM-DD');
    } else if (order && (!report.reportId || report.reportId && report.status === 'DRAFT') && daysBack && (order.hours_for_select_prev_day && (order.hours_for_select_prev_day / 24) < daysBack) || !order.hours_for_select_prev_day) {
        returnDate = moment(report && report.serviceDate ? serviceDate + ' ' + moment().format('HH:mm:ss') : null).subtract(daysBack, 'days').format('YYYY-MM-DD');
    } else {
        returnDate = order && order.hours_for_select_prev_day ?
            (moment(report && report.serviceDate ? serviceDate + ' ' + moment().format('HH:mm:ss') : null).subtract(order.hours_for_select_prev_day, 'hours').hour() < 6 ?
                moment(report && report.serviceDate ? serviceDate + ' ' + moment().format('HH:mm:ss') : null).subtract(order.hours_for_select_prev_day, 'hours').subtract(1, 'day').format('YYYY-MM-DD')
                : moment(report && report.serviceDate ? serviceDate + ' ' + moment().format('HH:mm:ss') : null).subtract(order.hours_for_select_prev_day, 'hours').format('YYYY-MM-DD'))
            : moment(report && report.serviceDate ? serviceDate + ' ' + moment().format('HH:mm:ss') : null).format('YYYY-MM-DD');
    }


    return returnDate;
}

export const removeReport = async (reportId: number | string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.delete(endpointPrefix + '/' + reportId + '/remove?profileId=' + profileId.value);
}

export const isSettingEnabled = (check: ReportSetting, order: Order) => {
    if (order.enable_nested_reporting && order.nested_reporting_settings) {
        let setting = order.nested_reporting_settings;
        return setting.includes(check);
    } else if (order.enable_single_components && order.single_components_settings) {
        let setting = order.single_components_settings;
        return setting.includes(check);
    }
}

export const getNumberOfComponent = (row: ReportRow, component: ReportRowComponent) => {
    const tmp = row.components?.slice().reverse();
    return tmp ? tmp.indexOf(component) + 1 : null;
}

export const validateHoursPassingMidnight = (worker: ReportRowWorker, report: Report, order: Order) => {
    let workingDayEnd = order.working_day_end ? order.working_day_end : '0600';
    if (order?.allow_add_report_forward) {
        return true;
    }
    if (worker.from) {
        let from = moment(worker.from, moment.ISO_8601).format('HHmm');
        let fromObject = moment(moment(report.serviceDate, moment.ISO_8601).format('DD.MM.YYYY') + ' ' + moment(worker.from, moment.ISO_8601).format('HH:mm'), 'DD.MM.YYYY HH:mm');
        fromObject.utcOffset(getUserTzOffset(), true);
        let workingDayEndDate = moment(moment(report.serviceDate, moment.ISO_8601).format('DD.MM.YYYY') + ' ' + workingDayEnd, 'DD.MM.YYYY HHmm');
        workingDayEndDate.utcOffset(getUserTzOffset(), true);
        let now = moment().utcOffset(getUserTzOffset());

        let tmp = workingDayEndDate.clone();
        tmp.add(1, 'day');
        workingDayEndDate = tmp;

        if (2400 !== parseInt(workingDayEnd) && from < parseInt(workingDayEnd)) {
            let tmp2 = fromObject.clone();
            tmp2.add(1, 'day');
            fromObject = tmp2;

            if (fromObject.isAfter(workingDayEndDate) || fromObject.isAfter(now)) {
                return fromObject;
            }
        } else {
            if (fromObject.isAfter(now)) {
                return fromObject;
            }
        }
    }

    return true;
}

export const getOrderFindErrorReadable = (error: string) => {
    if (error == 'SPEC_NO_NEWEST') {
        return 'reportsTab.orders.scanSpecificationPage.error.spec_no_newest';
    } else if (error == 'SPEC_NO_ACCEPTED') {
        return 'reportsTab.orders.scanSpecificationPage.error.spec_no_accepted';
    } else if (error == 'ORDER_BLOCKED') {
        return 'reportsTab.orders.scanSpecificationPage.error.order_blocked';
    } else if (error == 'ORDER_CLOSED') {
        return 'reportsTab.orders.scanSpecificationPage.error.order_closed';
    } else if (error == 'WORKER_NO_ALLOWED') {
        return 'reportsTab.orders.scanSpecificationPage.error.worker_no_allowed';
    } else if (error == 'WORKER_REPORTING_BLOCKED') {
        return 'reportsTab.orders.scanSpecificationPage.error.worker_reporting_blocked';
    } else if (error == 'CLIENT_REPORTING_NOT_ENABLE') {
        return 'reportsTab.orders.scanSpecificationPage.error.client_reporting_not_enable';
    } else if (error == 'ORDER_LIMITS_EXCEEDED') {
        return 'reportsTab.orders.scanSpecificationPage.error.order_limits_exceeded';
    } else if (error == 'ORDER_APT_NOT_ALLOWED') {
        return 'reportsTab.orders.scanSpecificationPage.error.order_apt_not_allowed';
    } else if (error == 'ORDER_BLOCKED_BY_STATUS') {
        return 'reportsTab.orders.scanSpecificationPage.error.order_blocked_by_status';
    } else if (error == 'WORKER_NOT_ENABLED') {
        return 'reportsTab.orders.scanSpecificationPage.error.worker_not_enabled';
    } else if (error == 'MULTIPLE_ORDERS_NOT_MATCH_TYPES') {
        return 'reportsTab.orders.scanSpecificationPage.error.multiple_orders_different_types';
    } else if (error == 'MULTIPLE_ORDERS_MULTIPLE_DISABLED') {
        return 'reportsTab.orders.scanSpecificationPage.error.multiple_orders_multiple_disabled';
    } else if (error == 'MULTIPLE_ORDERS_SAME_ORDER') {
        return 'reportsTab.orders.scanSpecificationPage.error.multiple_orders_same_order';
    } else if (error == 'MULTIPLE_ORDERS_SINGLE_COMPONENTS_DISABLED') {
        return 'reportsTab.orders.scanSpecificationPage.error.multiple_orders_single_components_disabled';
    } else if (error == 'WORKER_WITHOUT_AVAILABILITIES') {
        return 'reportsTab.orders.scanSpecificationPage.error.worker_without_availability';
    } else {
        return 'reportsTab.orders.scanSpecificationPage.error.common';
    }
}

export const validateQuarantinePeriod = (order: Order, serviceDate: string) => {
    if (!order || !order.days_for_quarantine) {
        return true;
    }

    return (getNumberOfWorkDays(moment(serviceDate).format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')) - 1) <= order.days_for_quarantine;
}

export const validateAvgTimePerComponent = (report: Report): boolean => {
    const checkedPartsSum = calculateCheckedPartsSum(report);
    const minutesSum = calculateControlMinutesSum(report);

    if (checkedPartsSum === 0) {
        return true;
    }

    return calculateAvgTimePerComponent(checkedPartsSum, minutesSum) > 0.01;
}

export const validateLogisticContainer = (maxInContainer = false, closeContainer = false): boolean => {
    if (!maxInContainer && closeContainer) {
        return false;
    }

    return true;
}


const calculateCheckedPartsSum = (report: Report): number => {
    let checkedPartsSum = 0;

    report.rows.forEach((row: ReportRow) => {
        if (row.quantity) {
            let quantity = typeof row.quantity === 'string' ? parseInt(row.quantity) : row.quantity;
            checkedPartsSum += quantity;
        }
    })

    return checkedPartsSum;
}

const calculateControlMinutesSum = (report: Report): number => {
    let minutesSum = 0;

    report.rows.forEach((row: ReportRow) => {
        row.workers?.forEach((worker: ReportRowWorker) => {
            if (worker.from && worker.to) {
                let fromDate = moment(worker.from);
                let toDate = moment(worker.to);
                if (toDate.isBefore(fromDate)) {
                    toDate.add(1, 'day');
                }
                let timeSpent = toDate.diff(fromDate, 'minutes') + 1;
                minutesSum += timeSpent;
            }
        })
    })

    return minutesSum;
}

const calculateAvgTimePerComponent = (checkedPartsSum: number, minutesSum: number): number => {
    if (minutesSum > 0 && checkedPartsSum > 0) {
        const avgTimePerComponent = (minutesSum * 60) / checkedPartsSum;

        return parseFloat(avgTimePerComponent.toFixed(2));
    }

    return 0;
}

const getReportTimepickerMinutesIterations = (order?: Order | null) => {
    if (order && order.use_time_iterations && order.reporting_iteration_type) {
        return getTimepickerMinutesIterations(order.reporting_iteration_type);
    }

    return getTimepickerMinutesIterations(TimepickerMinutesIterationsType.EVERY_5_MINUTES);
}

const getReportPrevMoment = (order?: Order | null) => {
    if (order && order.use_time_iterations && order.reporting_iteration_type) {
        switch (order.reporting_iteration_type) {
            case TimepickerMinutesIterationsType.EVERY_5_MINUTES:
                return prevMoment(5);
            case TimepickerMinutesIterationsType.EVERY_10_MINUTES:
                return prevMoment(10);
            case TimepickerMinutesIterationsType.EVERY_15_MINUTES:
                return prevMoment(15);
        }
    }

    return prevMoment();
}

const getReportNextMoment = (order?: Order | null) => {
    if (order && order.use_time_iterations && order.reporting_iteration_type) {
        switch (order.reporting_iteration_type) {
            case TimepickerMinutesIterationsType.EVERY_5_MINUTES:
                return nextMoment(5);
            case TimepickerMinutesIterationsType.EVERY_10_MINUTES:
                return nextMoment(10);
            case TimepickerMinutesIterationsType.EVERY_15_MINUTES:
                return nextMoment(15);
        }
    }

    return nextMoment();
}

export const compareWith = (o1: DataSelectable, o2: DataSelectable) => {
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
};

const internalCosts = async (orderId: number | string) => {
    let profileId = await Preferences.get({'key': 'profile_id'});
    return appApi.get(endpointPrefix + '/internal-costs/' + orderId + '?profileId=' + profileId.value);
}

export const isReportId = (reportId: string | number): boolean => {
    if (typeof reportId === "number") {
        return Number.isInteger(reportId) && reportId > 0;
    }

    if (/^\d+$/.test(reportId)) {
        return Number.isSafeInteger(Number(reportId)) && Number(reportId) > 0;
    }

    return false;
};

export interface BulkReportWorkerHours {
    report_id: number;
    workers: {
        row_index: number;
        worker_index: number;
        worker_number: string;
        from: string;
        to: string;
    }[];
}

const saveBulkReportWorkerHours = async (reports: BulkReportWorkerHours[]) => {
    return appApi.post(endpointPrefix + '/bulk-save-worker-hours', {
        reports
    }, {
        headers: {
            'No-Loader': true
        }
    });
}

export {
    getReports,
    checkSpecification,
    findContainerById,
    findSpecificationByNumber,
    findContainerByNumber,
    saveReport,
    postReport,
    editReport,
    getReport,
    validateReport,
    validateReportRowQuantity,
    transformReportToApiFormat,
    transformApiFormatToReport,
    orderComponentsNumbers,
    orderDefects,
    orderWorkers,
    checkWorker,
    getWorkerData,
    getPlaceOfServices,
    getPlaceOfServiceDepartments,
    getReportsCount,
    filtersToParams,
    filtersToParamsArray,
    filtersToParamsObjects,
    getFilterTypeForParamName,
    getNewestSpecification,
    getNewestImportantInfo,
    getWorkerRates,
    getPriceRates,
    findOrderByNumber,
    findOrderById,
    isEmptyReport,
    hoursHumanized,
    minReportDate,
    validateTimeOverlap,
    getReportTimepickerMinutesIterations,
    getReportPrevMoment,
    getReportNextMoment,
    internalCosts,
    saveBulkReportWorkerHours
};
